import { Fragment, useId, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';
import { cs } from '@bbkAdminUtils/css-utils';
import { useEqualHeightContext } from '@bbkPricingPage/components/equal-height-component/equal-height-context';

type EqualHeightComponentProps = {
  // Elements with the same groupId will have the same height
  groupId: string;
  children: JSX.Element;
  trackingObject?: unknown;
  ignore?: boolean;
};

const EqualHeightComponent = ({
  children,
  groupId,
  trackingObject,
  ignore,
}: EqualHeightComponentProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const componentId = useId();
  const { registerRef, rerender } = useEqualHeightContext();

  useIsomorphicLayoutEffect((): void => {
    registerRef(ref, componentId, groupId);
  }, []);

  useIsomorphicLayoutEffect(() => {
    rerender();
  }, [ref.current?.innerHTML, rerender, trackingObject, ignore]);

  useIsomorphicLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const measuredChild = ref.current?.children[0];
    if (!measuredChild) return;

    const observer = new ResizeObserver(() => {
      rerender();
    });
    observer.observe(measuredChild);
    return () => observer.disconnect();
  }, [rerender, ignore]);

  return (
    <Fragment>
      <div
        key={`non-ref-${ignore}`}
        className={cs(!ignore && 'tw-hidden')}
        data-ignore={ignore}
      >
        {children}
      </div>
      <div
        key={`ref-div-${ignore}`}
        ref={ref}
        data-group-id={groupId}
        data-ignore={ignore}
        className={cs(ignore && 'tw-hidden')}
      >
        {children}
      </div>
    </Fragment>
  );
};

export default EqualHeightComponent;
